import { z } from "zod";
import { IncomingReportSchema } from "./incoming-documents.js";
import { IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
import { StaffHandoffSummarySchema } from "./staff-requests.js";
const ResultCodeSchema = z.string().regex(/^[A-Z][A-Z0-9_]{0,79}$/);
export const OrderResultKindSchema = z.enum(["lab_result", "imaging_report"]);
export const OrderResultStatusSchema = z.enum(["preliminary", "final", "corrected", "cancelled"]);
export const OrderResultInterpretationSchema = z.enum([
    "normal",
    "abnormal",
    "critical",
    "unknown",
]);
export const OrderResultValueSchema = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("numeric"),
        value: z.number().finite(),
        unit: z.string().min(1).max(80).optional(),
        referenceLow: z.number().finite().optional(),
        referenceHigh: z.number().finite().optional(),
    })
        .strict()
        .superRefine((value, context) => {
        if (value.referenceLow !== undefined &&
            value.referenceHigh !== undefined &&
            value.referenceHigh < value.referenceLow) {
            context.addIssue({
                code: "custom",
                path: ["referenceHigh"],
                message: "The upper reference bound cannot be lower than the lower bound",
            });
        }
    }),
    z.object({ kind: z.literal("text"), value: z.string().min(1).max(4_000) }).strict(),
    z
        .object({
        kind: z.literal("coded"),
        code: z.string().min(1).max(120),
        display: z.string().min(1).max(240),
        codeSystem: z.string().min(1).max(160),
    })
        .strict(),
    z
        .object({
        kind: z.literal("attachment"),
        artifactId: IdSchema,
        version: z.number().int().positive(),
    })
        .strict(),
]);
export const InboundOrderResultObservationSchema = z
    .object({
    code: z.string().min(1).max(120),
    display: z.string().min(1).max(240),
    codeSystem: z.enum(["LOINC", "RadLex", "local", "free_text"]),
    value: OrderResultValueSchema,
    interpretation: OrderResultInterpretationSchema,
    observedAt: IsoDateTimeSchema,
    sourceReferenceRange: z.string().min(1).max(500).optional(),
})
    .strict();
export const OrderResultCorrelationSchema = z
    .object({
    expectedEventId: IdSchema.optional(),
    orderId: IdSchema.optional(),
    patientId: IdSchema.optional(),
    externalOrderId: z.string().min(1).max(240).optional(),
    accessionNumber: z.string().min(1).max(240).optional(),
})
    .strict()
    .superRefine((correlation, context) => {
    if (correlation.expectedEventId === undefined &&
        correlation.orderId === undefined &&
        correlation.externalOrderId === undefined &&
        correlation.accessionNumber === undefined) {
        context.addIssue({
            code: "custom",
            message: "At least one governed order-correlation key is required",
        });
    }
});
export const InboundOrderResultReportSchema = z
    .object({
    resultKind: OrderResultKindSchema,
    externalReportId: z.string().min(1).max(240),
    externalVersion: z.number().int().positive(),
    correctsExternalVersion: z.number().int().positive().optional(),
    sourceSystem: z.string().min(1).max(160),
    sourceDestinationId: IdSchema,
    sourceDestinationVersion: z.number().int().positive(),
    siteId: IdSchema,
    status: OrderResultStatusSchema,
    correlation: OrderResultCorrelationSchema,
    reportedAt: IsoDateTimeSchema,
    receivedAt: IsoDateTimeSchema,
    sourceDocumentHash: Sha256Schema,
    narrative: z.string().min(1).max(20_000).optional(),
    observations: z.array(InboundOrderResultObservationSchema).min(1).max(500),
})
    .strict()
    .superRefine((report, context) => {
    if ((report.status === "corrected") !== (report.correctsExternalVersion !== undefined)) {
        context.addIssue({
            code: "custom",
            path: ["correctsExternalVersion"],
            message: "Corrected reports must identify the external version they replace",
        });
    }
    if (report.correctsExternalVersion !== undefined &&
        report.correctsExternalVersion >= report.externalVersion) {
        context.addIssue({
            code: "custom",
            path: ["correctsExternalVersion"],
            message: "A correction must replace an earlier external report version",
        });
    }
    const keys = new Set();
    report.observations.forEach((observation, index) => {
        const key = `${observation.codeSystem}\u0000${observation.code}`;
        if (keys.has(key)) {
            context.addIssue({
                code: "custom",
                path: ["observations", index, "code"],
                message: "A report cannot repeat the same observation code",
            });
        }
        keys.add(key);
    });
});
export const OrderResultMatchSchema = z
    .object({
    matchId: IdSchema,
    expectedEventId: IdSchema,
    orderId: IdSchema,
    patientId: IdSchema,
    strategy: z.enum(["exact_external_id", "multi_key", "manual"]),
    confidence: z.number().min(0).max(1),
    matchedBy: IdSchema,
    matchedAt: IsoDateTimeSchema,
})
    .strict();
/** Partner feeds keep their strict verified-source/order correlation contract. */
export const ResultIngestionInputSchema = z.discriminatedUnion("source", [
    z
        .object({ source: z.literal("verified_partner"), report: InboundOrderResultReportSchema })
        .strict(),
    z.object({ source: z.literal("staff_upload"), report: IncomingReportSchema }).strict(),
]);
export const OrderResultReviewSchema = z
    .object({
    reviewId: IdSchema,
    reviewedBy: IdSchema,
    reviewedAt: IsoDateTimeSchema,
    disposition: z.enum([
        "no_action",
        "repeat_order",
        "follow_up",
        "medication_change",
        "urgent_escalation",
    ]),
    evidenceReferenceId: IdSchema,
    acknowledgedInterpretation: OrderResultInterpretationSchema,
})
    .strict();
export const OrderResultCommunicationSchema = z
    .object({
    communicationId: IdSchema,
    channel: z.enum(["phone", "secure_message", "in_person", "letter"]),
    outcome: z.enum(["reached", "caregiver_reached", "message_delivered"]),
    communicatedBy: IdSchema,
    communicatedAt: IsoDateTimeSchema,
    acknowledgementRequired: z.boolean(),
    acknowledgedAt: IsoDateTimeSchema.optional(),
})
    .strict();
export const OrderResultExceptionSchema = z
    .object({
    exceptionId: IdSchema,
    kind: z.enum([
        "unmatched",
        "ambiguous_match",
        "payload_conflict",
        "missing_result",
        "overdue",
        "critical_result",
        "corrected_after_review",
        "communication_failed",
    ]),
    state: z.enum(["open", "resolved"]),
    ownerId: IdSchema,
    openedAt: IsoDateTimeSchema,
    summary: z.string().min(1).max(500),
    resolvedAt: IsoDateTimeSchema.optional(),
})
    .strict();
export const OrderResultEscalationSchema = z
    .object({
    escalationId: IdSchema,
    reasonCode: ResultCodeSchema,
    ownerId: IdSchema,
    backupOwnerId: IdSchema,
    status: z.enum(["open", "resolved"]),
    createdAt: IsoDateTimeSchema,
    resolvedAt: IsoDateTimeSchema.optional(),
})
    .strict();
export const OrderResultWorkflowStatusSchema = z.enum([
    "received",
    "unmatched",
    "matched",
    "assigned_for_review",
    "reviewed",
    "patient_contact_pending",
    "closure_pending",
    "closed",
]);
export const OrderResultWorkflowSchema = z
    .object({
    tenantId: IdSchema,
    siteId: IdSchema,
    resultId: IdSchema,
    version: z.number().int().positive(),
    status: OrderResultWorkflowStatusSchema,
    ownerId: IdSchema,
    backupOwnerId: IdSchema,
    report: InboundOrderResultReportSchema,
    reportFingerprint: Sha256Schema,
    match: OrderResultMatchSchema.optional(),
    interpretation: OrderResultInterpretationSchema,
    communicationRequired: z.boolean(),
    acknowledgementRequired: z.boolean(),
    review: OrderResultReviewSchema.optional(),
    communication: OrderResultCommunicationSchema.optional(),
    exceptions: z.array(OrderResultExceptionSchema),
    escalations: z.array(OrderResultEscalationSchema),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    closedAt: IsoDateTimeSchema.optional(),
    closureReasonCode: ResultCodeSchema.optional(),
})
    .strict()
    .superRefine((workflow, context) => {
    if ((workflow.status === "closed") !== (workflow.closedAt !== undefined)) {
        context.addIssue({
            code: "custom",
            path: ["closedAt"],
            message: "Only closed result workflows may carry closure evidence",
        });
    }
    if ((workflow.status === "closed") !== (workflow.closureReasonCode !== undefined)) {
        context.addIssue({
            code: "custom",
            path: ["closureReasonCode"],
            message: "Closed result workflows require a closure reason",
        });
    }
});
export const OrderResultIngestionReceiptSchema = z
    .object({
    resultId: IdSchema,
    externalReportId: z.string().min(1).max(240),
    externalVersion: z.number().int().positive(),
    outcome: z.enum(["matched", "unmatched", "duplicate", "conflict", "corrected"]),
    workflowVersion: z.number().int().positive(),
    expectedEventId: IdSchema.optional(),
    recordedAt: IsoDateTimeSchema,
})
    .strict();
export const OrderResultWorkQueueItemSchema = z
    .object({
    workItemId: IdSchema,
    expectedEventId: IdSchema.optional(),
    resultId: IdSchema.optional(),
    version: z.number().int().positive(),
    resultKind: OrderResultKindSchema,
    status: OrderResultWorkflowStatusSchema,
    patientId: IdSchema.optional(),
    /** Who and which visit the result belongs to; absent while a result is unmatched. */
    patientName: z.string().min(1).max(240).optional(),
    encounterId: IdSchema.optional(),
    /**
     * Who owns the item (the reviewing clinician of a result, the owner of a missing-result
     * exception) and who covers for them, so Attention can show each person their own work.
     */
    ownerId: IdSchema.optional(),
    backupOwnerId: IdSchema.optional(),
    /** The open staff request handing this result to someone, who then sees the row too. */
    handoff: StaffHandoffSummarySchema.optional(),
    orderId: IdSchema.optional(),
    interpretation: OrderResultInterpretationSchema,
    displayName: z.string().min(1).max(240),
    expectedBy: IsoDateTimeSchema.optional(),
    overdueAt: IsoDateTimeSchema.optional(),
    exceptions: z.array(OrderResultExceptionSchema),
    /** When this report arrived, independent of later review or workflow updates. */
    receivedAt: IsoDateTimeSchema.optional(),
    latestUpdateAt: IsoDateTimeSchema,
})
    .strict();
export const OrderResultWorkQueueSchema = z
    .object({ items: z.array(OrderResultWorkQueueItemSchema) })
    .strict();
const ResultTransitionBaseSchema = z.object({
    expectedVersion: z.number().int().positive(),
    idempotencyKey: IdSchema,
});
export const OrderResultTransitionRequestSchema = z.discriminatedUnion("transition", [
    ResultTransitionBaseSchema.extend({
        transition: z.literal("matched"),
        expectedEventId: IdSchema,
        orderId: IdSchema,
        patientId: IdSchema,
        confidence: z.number().min(0).max(1),
    }),
    ResultTransitionBaseSchema.extend({
        transition: z.literal("reviewed"),
        disposition: OrderResultReviewSchema.shape.disposition,
        evidenceReferenceId: IdSchema,
    }),
    ResultTransitionBaseSchema.extend({
        transition: z.literal("patient_informed"),
        channel: OrderResultCommunicationSchema.shape.channel,
        outcome: OrderResultCommunicationSchema.shape.outcome,
        acknowledgementRequired: z.boolean(),
        acknowledgedAt: IsoDateTimeSchema.optional(),
    }),
    ResultTransitionBaseSchema.extend({
        transition: z.literal("escalated"),
        reasonCode: z.enum([
            "MISSING_RESULT",
            "OVERDUE",
            "CRITICAL_RESULT",
            "MATCH_CONFLICT",
            "COMMUNICATION_FAILED",
        ]),
    }),
    ResultTransitionBaseSchema.extend({
        transition: z.literal("closed"),
        closureReasonCode: z.enum(["COMPLETED", "NO_LONGER_INDICATED", "PATIENT_DECLINED"]),
    }),
]);
export const OrderResultTransitionCommandSchema = z.intersection(z.object({
    tenantId: IdSchema,
    siteId: IdSchema,
    actorId: IdSchema,
    resultId: IdSchema,
}), OrderResultTransitionRequestSchema);
export const OrderResultOverdueScanReceiptSchema = z
    .object({
    scannedAt: IsoDateTimeSchema,
    overdueExpectedEventIds: z.array(IdSchema),
    createdExceptionIds: z.array(IdSchema),
})
    .strict();
//# sourceMappingURL=results.js.map